import {
  type Configuration,
  type Snapshot,
  GetGwsBackupFilterApi,
  ListGoogleWorkspaceGroupsApi,
  ListGoogleWorkspaceOrgUnitsApi,
  ListInventoryItemSnapshotsApi,
  UpdateShouldFullBackupResourceApi,
  FileExplorerListPathApi,
  FileExplorerCountPathFilesApi,
} from '@repo/api-gw-sdk';
import { mutate } from 'swr';

import type { GWSnapshot } from '@/app/(dashboard)/saas-inventory/google-workspace/snapshotTypes';
import { dayjs } from '@/utils/dayjs';

import type { HttpClient } from './httpClient';

const backupSettingsUrl = '/inventory/backup-settings';
const orgUnitsUrl = '/saas-inventory/google-workspace/org-units';
const groupsUrl = '/saas-inventory/google-workspace/groups';
const snapshotsBaseUrl = '/saas-inventory/google-workspace';

const TRASH_PREFIX = 'trash/';
// One over the UI display threshold so the badge can render "10000+" without an exact count.
const TRASH_COUNT_THRESHOLD = 10001;

function snapshotToGWSnapshot(
  snapshot: Snapshot,
  isLatest: boolean
): GWSnapshot {
  const date = (snapshot.pointInTime ?? snapshot.createdTime).toISOString();
  return {
    id: snapshot.id,
    date,
    label: `${dayjs(date).format('MMM D, YYYY')}${isLatest ? ' (Latest)' : ''}`,
    sizeBytes: snapshot.fullSizeBytes ?? 0,
    userCount: 0,
    sharedDriveCount: 0,
  };
}

export const saasInventory = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const getBackupSettingsApi = new GetGwsBackupFilterApi(config);
  const updateBackupSettingsApi = new UpdateShouldFullBackupResourceApi(config);
  const listOrgUnitsApi = new ListGoogleWorkspaceOrgUnitsApi(config);
  const listGroupsApi = new ListGoogleWorkspaceGroupsApi(config);
  const listSnapshotsApi = new ListInventoryItemSnapshotsApi(config);
  const listPathApi = new FileExplorerListPathApi(config);
  const countPathFilesApi = new FileExplorerCountPathFilesApi(config);

  return {
    googleWorkspace: {
      useSnapshots: (resourceId: string | null) => {
        const result = httpClient.execute(
          listSnapshotsApi.listInventoryItemSnapshots.bind(listSnapshotsApi),
          resourceId ? snapshotsBaseUrl : null,
          'snapshots',
          currentProjectId,
          resourceId ?? '',
          0,
          100,
          'eon'
        );
        const snapshots: GWSnapshot[] = (result.data?.data ?? []).map(
          (s: Snapshot, i: number) => snapshotToGWSnapshot(s, i === 0)
        );
        return {
          data: snapshots,
          isLoading: result.isLoading,
        };
      },
      useListPath: (
        resourceId: string,
        prefix: string,
        pageSize: number,
        snapshotId?: string
      ) =>
        httpClient.execute(
          listPathApi.fileExplorerListPath.bind(listPathApi),
          resourceId ? snapshotsBaseUrl : null,
          'listPath',
          currentProjectId,
          resourceId,
          prefix,
          pageSize,
          undefined,
          undefined,
          snapshotId
        ),
      useTrashCount: (resourceId: string, snapshotId?: string) =>
        httpClient.execute(
          countPathFilesApi.fileExplorerCountPathFiles.bind(countPathFilesApi),
          resourceId && snapshotId ? snapshotsBaseUrl : null,
          'trashCount',
          currentProjectId,
          resourceId,
          TRASH_PREFIX,
          snapshotId,
          TRASH_COUNT_THRESHOLD
        ),
      useOrgUnits: (connectorId: string) =>
        httpClient.execute(
          listOrgUnitsApi.listGoogleWorkspaceOrgUnits.bind(listOrgUnitsApi),
          orgUnitsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useGroups: (connectorId: string) =>
        httpClient.execute(
          listGroupsApi.listGoogleWorkspaceGroups.bind(listGroupsApi),
          groupsUrl,
          connectorId,
          currentProjectId,
          connectorId
        ),
      useBackupSettings: (resourceId: string) =>
        httpClient.execute(
          getBackupSettingsApi.getGwsBackupFilter.bind(getBackupSettingsApi),
          backupSettingsUrl,
          resourceId,
          currentProjectId,
          resourceId
        ),
      updateBackupSettings: (resourceId: string, gwsFilterJson: string) =>
        updateBackupSettingsApi
          .updateShouldFullBackupResource(currentProjectId, resourceId, {
            gwsFilterJson,
          })
          .then(() =>
            mutate([
              backupSettingsUrl,
              resourceId,
              currentProjectId,
              resourceId,
            ])
          ),
    },
  };
};
